import React, { useState, useEffect } from "react";
import {
  FiXCircle,
  FiUser,
  FiPhone,
  // FiMail,
  FiEye,
  FiFilter,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
  FiCalendar,
  FiCreditCard,
  FiShield,
  FiAlertCircle,
  FiImage,
} from "react-icons/fi";
import { getKycRejectList } from "../../api/endpoints";
import PageHeader from "../../components/PageHeader";
import SectionHeader from "../../components/SectionHeader";
import { TableEmpty, TableLoading } from "../../components/TableState";
import notify from "../../utils/toast";

const KycRejectedList = () => {
  const [rejectedKyc, setRejectedKyc] = useState([]);
  const [selectedKyc, setSelectedKyc] = useState(null);
  const [baseUrl, setBaseUrl] = useState("");

  // Pagination & Filter States
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [imageErrors, setImageErrors] = useState({});

  // ✅ Fetch Rejected KYC from API
  const fetchRejectedKyc = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        search: search.trim(),
        startDate,
        endDate,
      };

      const res = await getKycRejectList(params);
      console.log("Rejected KYC Response:", res);

      // ✅ Save baseUrl from response
      if (res.data?.baseUrl) {
        setBaseUrl(res.data.baseUrl);
        console.log("Base URL set:", res.data.baseUrl);
      }

      setRejectedKyc(res.data.kycList || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (error) {
      console.error("Error fetching rejected KYC:", error);
      notify.error(error, "Could not load the rejected KYC list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRejectedKyc();
  }, [page, search, startDate, endDate]);

  // ✅ Format Date
  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ✅ Get Full Image URL - Handle both full URLs and relative paths
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    // If image already has full URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    // If baseUrl exists, append the image path
    if (baseUrl) {
      const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
      return `${cleanBaseUrl}/${imagePath}`;
    }
    return imagePath;
  };

  // ✅ Handle image error
  const handleImageError = (imageKey) => {
    setImageErrors(prev => ({ ...prev, [imageKey]: true }));
  };

  return (
    <div className="kyc-rejected-page">
      {/* Header */}
      <PageHeader
        icon={<FiXCircle />}
        tone="danger"
        title="KYC rejected"
        subtitle="Requests that were turned down, with the reason each player was given."
        stat={{ label: "Rejected", value: total }}
      />

      {/* Card */}
      <div className="card shadow-sm border-0">
        {/* Card Header - Search/Filter */}
        <SectionHeader
          title="Rejected requests"
          count={total}
          filtersOpen={showFilter}
          actions={
            <>
              <button
                className={`btn btn-sm ${showFilter ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => setShowFilter(!showFilter)}
                aria-expanded={showFilter}
              >
                <FiFilter className="me-1" />
                Filter
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={fetchRejectedKyc}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <>
                    <FiRefreshCw className="me-1" />
                    Refresh
                  </>
                )}
              </button>
            </>
          }
          filters={
            <>
              <span className="section-header__filter-label">Search</span>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Name or mobile number"
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
              <span className="section-header__filter-label">From</span>
              <input
                type="date"
                className="form-control form-control-sm"
                value={startDate}
                onChange={(e) => {
                  setPage(1);
                  setStartDate(e.target.value);
                }}
              />
              <span className="section-header__filter-label">To</span>
              <input
                type="date"
                className="form-control form-control-sm"
                value={endDate}
                onChange={(e) => {
                  setPage(1);
                  setEndDate(e.target.value);
                }}
              />
              {(search || startDate || endDate) && (
                <button
                  className="btn btn-link btn-sm text-decoration-none"
                  onClick={() => {
                    setPage(1);
                    setSearch("");
                    setStartDate("");
                    setEndDate("");
                  }}
                >
                  Clear
                </button>
              )}
            </>
          }
        />

        {/* Table */}
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th style={{ width: "50px" }}>#</th>
                  <th style={{ minWidth: "200px" }}>Player</th>
                  <th style={{ minWidth: "150px" }}>Document</th>
                  <th style={{ minWidth: "150px" }}>Document no.</th>
                  <th style={{ minWidth: "200px" }}>Reason</th>
                  <th style={{ minWidth: "150px" }}>Rejected</th>
                  <th style={{ minWidth: "150px" }}>Rejected by</th>
                  <th style={{ width: "120px" }}>Status</th>
                  <th style={{ width: "120px" }} className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableLoading colSpan={9} label="Loading rejected requests…" />
                ) : rejectedKyc.length > 0 ? (
                  rejectedKyc.map((item, index) => (
                    <tr key={item._id}>
                      <td>{(page - 1) * limit + index + 1}</td>

                      {/* User Details */}
                      <td>
                        <div className="table-identity">
                          <span className="table-identity__name">
                            <FiUser size={13} className="text-danger" />
                            {item.fullName || "—"}
                          </span>
                          <span className="table-identity__meta">
                            <FiPhone size={11} />
                            {item.user_mobile_no || item.mobileNumber || "—"}
                          </span>
                        </div>
                      </td>

                      {/* Document Type */}
                      <td>
                        <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25">
                          {item.documentType || "N/A"}
                        </span>
                      </td>

                      {/* Document Number */}
                      <td>
                        <span className="d-inline-flex align-items-center gap-1">
                          <FiCreditCard size={13} className="text-muted" />
                          {item.documentNumber || "—"}
                        </span>
                      </td>

                      {/* Rejection Reason */}
                      <td>
                        <div className="d-flex align-items-center text-danger small">
                          <FiAlertCircle size={14} className="me-1" />
                          {item.rejectionReason || "No reason provided"}
                        </div>
                      </td>

                      {/* Rejected At */}
                      <td>
                        <div className="small text-danger">
                          <FiCalendar size={10} className="me-1" />
                          {formatDate(item.verifiedAt || item.updatedAt)}
                        </div>
                      </td>

                      {/* Rejected By */}
                      <td>
                        <div className="small">
                          <FiShield size={10} className="me-1" />
                          {item.verifiedBy ? `Admin (${item.verifiedBy.slice(-4)})` : "System"}
                        </div>
                      </td>

                      {/* Status */}
                      <td>
                        <span className="badge bg-danger">
                          <FiXCircle /> Rejected
                        </span>
                      </td>

                      {/* Action (View Documents) */}
                      <td>
                        <div className="table-actions">
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => {
                              setSelectedKyc(item);
                              setImageErrors({});
                            }}
                          >
                            <FiEye className="me-1" />
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <TableEmpty
                    colSpan={9}
                    icon={<FiXCircle />}
                    title="No rejected requests"
                    message={
                      search || startDate || endDate
                        ? "Nothing matches these filters. Clear them to see everything."
                        : "Requests you turn down from the pending list will be listed here."
                    }
                  />
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="table-pagination">
              <span className="table-pagination__summary">
                Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of{" "}
                {total}
              </span>
              <div className="table-pagination__controls">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  <FiChevronLeft /> Previous
                </button>
                <span className="table-pagination__page">
                  Page {page} of {totalPages}
                </span>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next <FiChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ------------------- KYC Image Modal - WITH crossOrigin="anonymous" ------------------- */}
      {selectedKyc && (
        <div
          className="modal show d-block"
          style={{
            backgroundColor: "rgba(0,0,0,0.7)",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
          onClick={() => setSelectedKyc(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg"
            style={{ maxWidth: "800px", width: "100%", margin: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title">KYC Documents</h5>
                  <div className="small text-muted">
                    {selectedKyc.fullName} | Mobile: {selectedKyc.user_mobile_no || selectedKyc.mobileNumber || "N/A"}
                  </div>
                  <div className="small text-primary fw-bold mt-1">
                    <FiCreditCard /> Doc No: {selectedKyc.documentNumber || "N/A"}
                  </div>
                  {selectedKyc.rejectionReason && (
                    <div className="small text-danger fw-bold mt-1">
                      <FiAlertCircle /> Reason: {selectedKyc.rejectionReason}
                    </div>
                  )}
                </div>
                <button
                  className="btn-close"
                  onClick={() => setSelectedKyc(null)}
                />
              </div>

              <div className="modal-body">
                <div className="row g-3">
                  {/* Aadhaar Front */}
                  <div className="col-md-4 text-center">
                    <h6 className="mb-2">Aadhaar Front</h6>
                    {selectedKyc.aadhaarFrontImage ? (
                      !imageErrors[`${selectedKyc._id}-front`] ? (
                        <div style={{ height: "200px", position: "relative", backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
                          <img
                            src={getImageUrl(selectedKyc.aadhaarFrontImage)}
                            crossOrigin="anonymous"
                            className="img-fluid rounded border"
                            style={{ 
                              height: "200px", 
                              width: "100%", 
                              objectFit: "contain",
                              backgroundColor: "#f8f9fa"
                            }}
                            alt="Aadhaar Front"
                            onLoad={() => console.log("Aadhaar Front loaded:", selectedKyc.aadhaarFrontImage)}
                            onError={(e) => {
                              console.error("Aadhaar Front failed to load:", selectedKyc.aadhaarFrontImage);
                              handleImageError(`${selectedKyc._id}-front`);
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      ) : (
                        <div className="text-muted p-3 border rounded" style={{ height: "200px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f8f9fa" }}>
                          <div>
                            <FiImage size={36} className="text-muted" />
                            <small>Image not available</small>
                            <br />
                            <small style={{ fontSize: "10px", color: "#999" }}>
                              {selectedKyc.aadhaarFrontImage?.split('/').pop()}
                            </small>
                          </div>
                        </div>
                      )
                    ) : (
                      <div className="text-muted p-3 border rounded" style={{ height: "200px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f8f9fa" }}>
                        <small>No image uploaded</small>
                      </div>
                    )}
                  </div>

                  {/* Aadhaar Back */}
                  <div className="col-md-4 text-center">
                    <h6 className="mb-2">Aadhaar Back</h6>
                    {selectedKyc.aadhaarBackImage ? (
                      !imageErrors[`${selectedKyc._id}-back`] ? (
                        <div style={{ height: "200px", position: "relative", backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
                          <img
                            src={getImageUrl(selectedKyc.aadhaarBackImage)}
                            crossOrigin="anonymous"
                            className="img-fluid rounded border"
                            style={{ 
                              height: "200px", 
                              width: "100%", 
                              objectFit: "contain",
                              backgroundColor: "#f8f9fa"
                            }}
                            alt="Aadhaar Back"
                            onLoad={() => console.log("Aadhaar Back loaded:", selectedKyc.aadhaarBackImage)}
                            onError={(e) => {
                              console.error("Aadhaar Back failed to load:", selectedKyc.aadhaarBackImage);
                              handleImageError(`${selectedKyc._id}-back`);
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      ) : (
                        <div className="text-muted p-3 border rounded" style={{ height: "200px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f8f9fa" }}>
                          <div>
                            <FiImage size={36} className="text-muted" />
                            <small>Image not available</small>
                            <br />
                            <small style={{ fontSize: "10px", color: "#999" }}>
                              {selectedKyc.aadhaarBackImage?.split('/').pop()}
                            </small>
                          </div>
                        </div>
                      )
                    ) : (
                      <div className="text-muted p-3 border rounded" style={{ height: "200px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f8f9fa" }}>
                        <small>No image uploaded</small>
                      </div>
                    )}
                  </div>

                  {/* PAN Card */}
                  <div className="col-md-4 text-center">
                    <h6 className="mb-2">PAN Card</h6>
                    {selectedKyc.panImage ? (
                      !imageErrors[`${selectedKyc._id}-pan`] ? (
                        <div style={{ height: "200px", position: "relative", backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
                          <img
                            src={getImageUrl(selectedKyc.panImage)}
                            crossOrigin="anonymous"
                            className="img-fluid rounded border"
                            style={{ 
                              height: "200px", 
                              width: "100%", 
                              objectFit: "contain",
                              backgroundColor: "#f8f9fa"
                            }}
                            alt="PAN Card"
                            onLoad={() => console.log("PAN Card loaded:", selectedKyc.panImage)}
                            onError={(e) => {
                              console.error("PAN Card failed to load:", selectedKyc.panImage);
                              handleImageError(`${selectedKyc._id}-pan`);
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      ) : (
                        <div className="text-muted p-3 border rounded" style={{ height: "200px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f8f9fa" }}>
                          <div>
                            <FiImage size={36} className="text-muted" />
                            <small>Image not available</small>
                            <br />
                            <small style={{ fontSize: "10px", color: "#999" }}>
                              {selectedKyc.panImage?.split('/').pop()}
                            </small>
                          </div>
                        </div>
                      )
                    ) : (
                      <div className="text-muted p-3 border rounded" style={{ height: "200px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f8f9fa" }}>
                        <small>No image uploaded</small>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setSelectedKyc(null)}
                >
                  Close
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    const images = [
                      selectedKyc.aadhaarFrontImage,
                      selectedKyc.aadhaarBackImage,
                      selectedKyc.panImage
                    ].filter(url => url);
                    
                    if (images.length > 0) {
                      window.open(getImageUrl(images[0]), "_blank");
                    } else {
                      notify.info("No documents were uploaded with this request.", "Nothing to open");
                    }
                  }}
                >
                  View Full Size
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KycRejectedList;